import { createContext, useCallback, useContext, useEffect, useState } from "react";

import toast from "components/ui/toast/Toast";
import { useBidResponseExtractionStatus } from "lib/hooks/api/bid/bid_responses/useBidResponseExtractionStatus";
import { useStartBidResponsesExtract } from "lib/hooks/api/bid/bid_responses/useStartBidResponsesExtract";
import { useHoldStaleBundleReload } from "lib/staleBundleReload";

export type BidResponseProcessingStatus = Record<
  string,
  { status: "processing" | "completed" | "error"; message?: string; questionsExtracted?: number }
>;

function allDone(status: BidResponseProcessingStatus) {
  return Object.values(status).every((s) => s.status === "completed" || s.status === "error");
}

type ExtractionState = {
  status: BidResponseProcessingStatus;
  isExtracting: boolean;
};

const DEFAULT_EXTRACTION_STATE: ExtractionState = { status: {}, isExtracting: false };

type BidResponsesProcessingContextType = {
  activeBidId: string | undefined;
  startExtraction: (bidId: string, documentIds: string[]) => void;
  getStatus: (bidId: string) => BidResponseProcessingStatus;
  getIsExtracting: (bidId: string) => boolean;
  setActiveBidId: (bidId: string) => void;
};

const BidResponseProcessingContext = createContext<BidResponsesProcessingContextType | undefined>(
  undefined,
);

export function BidResponsesProcessingProvider({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element {
  const [activeBidId, setActiveBidId] = useState<string | undefined>();
  const [extractionStates, setExtractionStates] = useState<Record<string, ExtractionState>>({});

  const updateExtractionState = useCallback(
    (bidId: string, updater: (prev: ExtractionState) => ExtractionState) => {
      setExtractionStates((prev) => ({
        ...prev,
        [bidId]: updater(prev[bidId] ?? DEFAULT_EXTRACTION_STATE),
      }));
    },
    [],
  );

  const { mutate: extractQuestions } = useStartBidResponsesExtract({
    onError: (_error, variables) => {
      const bidId = variables?.input?.bidId;
      if (!bidId) {
        toast.error("Error starting extraction");
        return;
      }

      updateExtractionState(bidId, () => ({
        status: { error: { status: "error", message: "Failed to start extraction" } },
        isExtracting: false,
      }));

      toast.error("Error starting extraction");
    },
  });

  const startExtraction = useCallback(
    (bidId: string, documentIds: string[]) => {
      setActiveBidId(bidId);

      const initialStatus = documentIds.reduce<BidResponseProcessingStatus>((acc, id) => {
        acc[id] = { status: "processing" };
        return acc;
      }, {});

      updateExtractionState(bidId, () => ({
        status: initialStatus,
        isExtracting: true,
      }));

      extractQuestions({ input: { bidId } });
    },
    [extractQuestions, updateExtractionState],
  );

  const getStatus = useCallback(
    (bidId: string) => extractionStates[bidId]?.status ?? {},
    [extractionStates],
  );

  const getIsExtracting = useCallback(
    (bidId: string) => extractionStates[bidId]?.isExtracting ?? false,
    [extractionStates],
  );

  const isBidResponseExtractionEnabled =
    !!activeBidId && (extractionStates[activeBidId]?.isExtracting ?? false);
  // Extraction progress is only tracked here, so a reload would lose it.
  useHoldStaleBundleReload(isBidResponseExtractionEnabled);

  const handleDocumentProcessed = useCallback(
    ({
      documentId,
      questionsExtracted,
      message,
    }: {
      documentId: string;
      questionsExtracted: number;
      message: string | null;
    }) => {
      if (!activeBidId) return;
      updateExtractionState(activeBidId, (prev) => {
        const newStatus: BidResponseProcessingStatus = {
          ...prev.status,
          [documentId]: { status: "completed", questionsExtracted, message: message || undefined },
        };
        const isDone = allDone(newStatus);
        if (isDone) toast.success("Question extraction completed");
        return { status: newStatus, isExtracting: !isDone };
      });
    },
    [activeBidId, updateExtractionState],
  );

  const handleError = useCallback(
    ({ documentId, message }: { documentId: string; message: string | null }) => {
      if (!activeBidId) return;
      updateExtractionState(activeBidId, (prev) => {
        const newStatus: BidResponseProcessingStatus = {
          ...prev.status,
          [documentId]: { status: "error", message: message || undefined },
        };
        const isDone = allDone(newStatus);
        if (isDone) toast.warning("Question extraction completed with errors");
        return { status: newStatus, isExtracting: !isDone };
      });
    },
    [activeBidId, updateExtractionState],
  );

  const handleStatusUpdate = useCallback(
    ({ documentId, message }: { documentId: string; message: string | null }) => {
      if (!activeBidId) return;
      updateExtractionState(activeBidId, (prev) => ({
        status: {
          ...prev.status,
          [documentId]: { status: "processing", message: message || undefined },
        },
        isExtracting: true,
      }));
    },
    [activeBidId, updateExtractionState],
  );

  useBidResponseExtractionStatus({
    resourceId: activeBidId,
    isEnabled: isBidResponseExtractionEnabled,
    onDocumentProcessed: handleDocumentProcessed,
    onError: handleError,
    onStatusUpdate: handleStatusUpdate,
  });

  return (
    <BidResponseProcessingContext.Provider
      value={{ activeBidId, startExtraction, getStatus, getIsExtracting, setActiveBidId }}
    >
      {children}
    </BidResponseProcessingContext.Provider>
  );
}

export const useBidResponseProcessingContext = (bidId?: string) => {
  const context = useContext(BidResponseProcessingContext);

  // In some places (e.g. a global bid responses view) we can render the table
  // without having a bid/opportunity scope, in which case extraction isn't available.

  useEffect(() => {
    if (!bidId || !context) return;
    if (context.activeBidId !== bidId) {
      context.setActiveBidId(bidId);
    }
  }, [context, bidId]);

  if (!bidId) {
    return {
      startExtraction: (_docIds: string[]) => {},
      status: {},
      isExtracting: false,
    };
  }

  if (!context) {
    throw new Error(
      "useBidResponseProcessingContext must be used within a BidResponsesProcessingProvider",
    );
  }

  return {
    startExtraction: (docIds: string[]) => context.startExtraction(bidId, docIds),
    status: context.getStatus(bidId),
    isExtracting: context.getIsExtracting(bidId),
  };
};
